/* global document, requestAnimationFrame, setTimeout, window */

import type { ReportFilterFieldSpec } from '../reporting-target-filter.types';

const VIEWPORT_EDGE_GAP_PX = 8;
/** Padding + border under/above the scrollable list (fudge for max-height math). */
const PANEL_CHROME_PX = 28;
const LIST_MIN_SCROLL_PX = 80;

/**
 * Checkbox-style multiselect: closed trigger shows summary; panel lists options with checkboxes (Material-like).
 */
export function createReportMultiselectElement(spec: ReportFilterFieldSpec): HTMLElement {
    if (spec.kind !== 'multiselect') {
        throw new Error('createReportMultiselectElement: expected multiselect spec');
    }

    const options = spec.options ?? [];
    const selectedByDefault = new Set(spec.defaultSelectedValues ?? []);
    const panelId = `${spec.elementId}-panel`;
    const triggerId = `${spec.elementId}-trigger`;
    const helperText = spec.helperText;
    const helperId = helperText ? `${spec.elementId}-helper` : undefined;

    const root = document.createElement('div');
    root.className = 'report-multiselect';
    root.id = spec.elementId;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = triggerId;
    trigger.className = 'report-multiselect__trigger field-input';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', panelId);
    if (helperId) {
        trigger.setAttribute('aria-describedby', helperId);
    }

    const valueSpan = document.createElement('span');
    valueSpan.className = 'report-multiselect__value';
    valueSpan.textContent = spec.placeholder ?? 'Select…';
    valueSpan.classList.add('report-multiselect__value--placeholder');

    const chevron = document.createElement('span');
    chevron.className = 'report-multiselect__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    trigger.append(valueSpan, chevron);

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'report-multiselect__panel hidden';
    panel.setAttribute('role', 'group');
    panel.setAttribute('aria-label', spec.label);
    panel.setAttribute('aria-hidden', 'true');

    const list = document.createElement('div');
    list.className = 'report-multiselect__list';
    const rowCap = spec.visibleSize ?? 6;
    list.style.maxHeight = `min(45vh, ${Math.min(12, Math.max(3, rowCap)) * 2.75}rem)`;
    list.dataset.baseMaxHeight = list.style.maxHeight;

    for (const opt of options) {
        const row = document.createElement('label');
        row.className = 'report-multiselect__option';

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = opt.value;
        cb.checked = selectedByDefault.has(opt.value);

        const text = document.createElement('span');
        text.className = 'report-multiselect__option-label';
        text.textContent = opt.label;

        row.append(cb, text);
        list.append(row);
    }

    panel.append(list);
    root.append(trigger, panel);

    if (helperText && helperId) {
        const hint = document.createElement('p');
        hint.id = helperId;
        hint.className = 'hint report-multiselect__helper';
        hint.textContent = helperText;
        root.append(hint);
    }

    wireReportMultiselect(root, spec);
    return root;
}

function wireReportMultiselect(root: HTMLElement, spec: ReportFilterFieldSpec): void {
    if (spec.kind !== 'multiselect') {
        return;
    }

    const trigger = root.querySelector<HTMLButtonElement>('.report-multiselect__trigger');
    const panel = root.querySelector<HTMLElement>('.report-multiselect__panel');
    const valueEl = root.querySelector<HTMLElement>('.report-multiselect__value');
    const checkboxes = root.querySelectorAll<HTMLInputElement>(
        '.report-multiselect__option input[type="checkbox"]'
    );

    if (!trigger || !panel || !valueEl) {
        return;
    }

    const options = spec.options ?? [];
    const placeholder = spec.placeholder ?? 'Select…';

    function selectedValues(): string[] {
        return Array.from(checkboxes)
            .filter((cb) => cb.checked)
            .map((cb) => cb.value.trim())
            .filter((v) => v.length > 0);
    }

    function updateSummary(): void {
        const values = selectedValues();
        const labels = values.map(
            (v) => options.find((o) => o.value === v)?.label ?? v
        );
        if (labels.length === 0) {
            valueEl.textContent = placeholder;
            valueEl.classList.add('report-multiselect__value--placeholder');
        } else {
            valueEl.classList.remove('report-multiselect__value--placeholder');
            if (labels.length <= 2) {
                valueEl.textContent = labels.join(', ');
            } else {
                valueEl.textContent = `${labels.length} selected`;
            }
        }
    }

    let docListener: ((e: MouseEvent) => void) | null = null;

    const listEl = panel.querySelector<HTMLElement>('.report-multiselect__list');

    function resetPanelPlacement(): void {
        panel.classList.remove('report-multiselect__panel--drop-up');
        if (listEl && listEl.dataset.baseMaxHeight !== undefined) {
            listEl.style.maxHeight = listEl.dataset.baseMaxHeight;
        }
    }

    function close(): void {
        panel.classList.add('hidden');
        panel.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        resetPanelPlacement();
        if (docListener) {
            document.removeEventListener('click', docListener);
            docListener = null;
        }
    }

    function viewportHeight(): number {
        return window.visualViewport?.height ?? window.innerHeight;
    }

    function placeOpenPanel(): void {
        resetPanelPlacement();

        const triggerRect = trigger.getBoundingClientRect();
        const vh = viewportHeight();
        const spaceBelow = vh - triggerRect.bottom - VIEWPORT_EDGE_GAP_PX;
        const spaceAbove = triggerRect.top - VIEWPORT_EDGE_GAP_PX;
        const panelHeight = panel.offsetHeight;

        const fitsBelow = panelHeight <= spaceBelow;
        const fitsAbove = panelHeight <= spaceAbove;

        if (fitsBelow) {
            return;
        }
        if (fitsAbove) {
            panel.classList.add('report-multiselect__panel--drop-up');
            return;
        }

        const openUp = spaceAbove > spaceBelow;
        const room = Math.max(0, (openUp ? spaceAbove : spaceBelow) - PANEL_CHROME_PX);
        const listCap = Math.max(LIST_MIN_SCROLL_PX, room);
        if (listEl) {
            listEl.style.maxHeight = `${listCap}px`;
        }
        if (openUp) {
            panel.classList.add('report-multiselect__panel--drop-up');
        }
    }

    function open(): void {
        panel.classList.remove('hidden');
        panel.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => {
            placeOpenPanel();
        });
        docListener = (e: MouseEvent) => {
            if (!root.contains(e.target as Node)) {
                close();
            }
        };
        setTimeout(() => document.addEventListener('click', docListener), 0);
    }

    function toggle(): void {
        if (panel.classList.contains('hidden')) {
            open();
        } else {
            close();
        }
    }

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        toggle();
    });

    trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            close();
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    });

    panel.addEventListener('click', (e) => e.stopPropagation());

    checkboxes.forEach((cb) => cb.addEventListener('change', updateSummary));
    updateSummary();
}
